import React from "react";
import { AlertTriangle, Bot, MessageSquare, ShoppingBag, UserCheck, UserX, Cpu, ArrowUpRight } from "lucide-react";
import type { ConversationKpis } from "../../types";

interface ConversationKpiGridProps {
  kpis: ConversationKpis;
  capabilities: {
    has_handoff: boolean;
    has_sales: boolean;
    has_llm: boolean;
    has_vendor_buffer: boolean;
    wbot_outbox_window: number;
  };
  onFilterResolution?: (resolution: string) => void;
}

export const ConversationKpiGrid: React.FC<ConversationKpiGridProps> = ({
  kpis,
  capabilities,
  onFilterResolution,
}) => {
  const n = kpis.conversations_started;

  return (
    <div className="conversation-kpi-container" style={{ marginBottom: "24px" }}>
      {/* Grilla de Tarjetas KPI (§5.2) */}
      <div
        className="kpi-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "14px",
          marginBottom: "16px",
        }}
      >
        {/* Tarjeta 1: Iniciadas */}
        <div
          className="kpi-card"
          onClick={() => onFilterResolution?.("all")}
          style={{
            cursor: onFilterResolution ? "pointer" : "default",
            borderLeft: "4px solid #3b82f6",
            background: "var(--bg-surface, #111827)",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
            borderLeftWidth: "4px",
            borderLeftColor: "#3b82f6",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
              Conversaciones iniciadas
            </span>
            <MessageSquare size={16} color="#3b82f6" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
            {n.toLocaleString("es-CL")}
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
            <strong style={{ color: "var(--text-primary)" }}>{kpis.avg_client_messages.toLocaleString("es-CL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</strong> mensajes del visitante en promedio
          </div>
        </div>

        {/* Tarjeta 2: Derivación (Requiere has_handoff) */}
        {capabilities.has_handoff && (
          <div
            className="kpi-card"
            onClick={() => onFilterResolution?.("handed_off")}
            style={{
              cursor: onFilterResolution ? "pointer" : "default",
              background: "var(--bg-surface, #111827)",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
              borderLeftWidth: "4px",
              borderLeftColor: "#1d5f96",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
                Terminaron en derivación
              </span>
              <UserCheck size={16} color="#1d5f96" />
            </div>
            <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
              {kpis.handed_off_count.toLocaleString("es-CL")}{" "}
              <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--text-muted)" }}>
                · {kpis.handed_off_pct}%
              </span>
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
              <strong style={{ color: "#5e3a8f" }}>{kpis.handed_off_direct_count}</strong> de inmediato ·{" "}
              <strong>{kpis.was_handed_off_count}</strong> pasaron por derivación
            </div>
          </div>
        )}

        {/* Tarjeta 3: Compra (Requiere has_sales) */}
        {capabilities.has_sales && (
          <div
            className="kpi-card"
            onClick={() => onFilterResolution?.("converted")}
            style={{
              cursor: onFilterResolution ? "pointer" : "default",
              background: "var(--bg-surface, #111827)",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
              borderLeftWidth: "4px",
              borderLeftColor: "#1a6e33",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
                Terminaron en compra
              </span>
              <ShoppingBag size={16} color="#1a6e33" />
            </div>
            <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
              {kpis.converted_count.toLocaleString("es-CL")}{" "}
              <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--text-muted)" }}>
                · {kpis.converted_pct}%
              </span>
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
              {kpis.amount_converted > 0 ? (
                <>
                  <strong style={{ color: "#10b981" }}>${kpis.amount_converted.toLocaleString("es-CL")}</strong> registrados
                </>
              ) : (
                "Sin ventas registradas en el período"
              )}
            </div>
          </div>
        )}

        {/* Tarjeta 4: Abandono */}
        <div
          className="kpi-card"
          onClick={() => onFilterResolution?.("lost")}
          style={{
            cursor: onFilterResolution ? "pointer" : "default",
            background: "var(--bg-surface, #111827)",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
            borderLeftWidth: "4px",
            borderLeftColor: "#a02626",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
              El visitante se fue
            </span>
            <UserX size={16} color="#a02626" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
            {kpis.lost_count.toLocaleString("es-CL")}{" "}
            <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--text-muted)" }}>
              · {kpis.lost_pct}%
            </span>
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
            No quiso seguir o no compró
          </div>
        </div>

        {/* Tarjeta 5: Sin resolución (Abierta) */}
        <div
          className="kpi-card"
          onClick={() => onFilterResolution?.("open")}
          style={{
            cursor: onFilterResolution ? "pointer" : "default",
            background: "var(--bg-surface, #111827)",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
            borderLeftWidth: "4px",
            borderLeftColor: "#a7aaad",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
              Sin resolución
            </span>
            <Bot size={16} color="#a7aaad" />
          </div>
          <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
            {kpis.open_count.toLocaleString("es-CL")}{" "}
            <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--text-muted)" }}>
              · {kpis.open_pct}%
            </span>
          </div>
          <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
            Se muestran como Abierta {kpis.stale_count > 0 ? `(${kpis.stale_count} inactivas)` : ""}
          </div>
        </div>

        {/* Tarjeta 6: Tokens Usados (Requiere has_llm) */}
        {capabilities.has_llm && (
          <div
            className="kpi-card"
            style={{
              background: "var(--bg-surface, #111827)",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
              borderLeftWidth: "4px",
              borderLeftColor: "#818cf8",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
                Tokens usados
              </span>
              <Cpu size={16} color="#818cf8" />
            </div>
            <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary, #f9fafb)", marginBottom: "4px" }}>
              {kpis.tokens_total.toLocaleString("es-CL")}
            </div>
            <div style={{ fontSize: "11px", color: "var(--text-muted, #9ca3af)", lineHeight: 1.4 }}>
              {kpis.tokens_in.toLocaleString("es-CL")} in · {kpis.tokens_out.toLocaleString("es-CL")} out · {kpis.tokens_avg_per_conv.toLocaleString("es-CL")} / conv. ·{" "}
              <strong style={{ color: "#10b981" }}>US${kpis.cost_usd_est.toFixed(2)} est.</strong>
            </div>
          </div>
        )}

        {/* Tarjeta 7: MPT Mensajes por envío (Requiere has_handoff) */}
        {capabilities.has_handoff && (
          <div
            className="kpi-card"
            style={{
              background: "var(--bg-surface, #111827)",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
              borderLeftWidth: "4px",
              borderLeftColor: kpis.mpt_alert ? "#f59e0b" : "rgba(255,255,255,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>
                Mensajes por envío (MPT)
              </span>
              {kpis.mpt_alert ? (
                <span title="MPT > 1.5: Mensajes fragmentados">
                  <AlertTriangle size={16} color="#f59e0b" />
                </span>
              ) : (
                <ArrowUpRight size={16} color="var(--text-muted)" />
              )}
            </div>
            <div
              style={{
                fontSize: "28px",
                fontWeight: 700,
                color: kpis.mpt_alert ? "#f59e0b" : "var(--text-primary, #f9fafb)",
                marginBottom: "4px",
              }}
            >
              {kpis.mpt !== null ? kpis.mpt.toFixed(2) : "—"}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-muted, #9ca3af)" }}>
              <strong>{kpis.msgs_agent}</strong> mensajes de vendedores → <strong>{kpis.agent_deliveries}</strong> envíos al cliente
            </div>
          </div>
        )}
      </div>

      {/* Aviso Contextual de Ventana de Agrupación (§5.2) */}
      {kpis.window_alert_visible && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "12px 16px",
            borderRadius: "8px",
            background: "rgba(245, 158, 11, 0.08)",
            border: "1px solid rgba(245, 158, 11, 0.25)",
            color: "#fbbf24",
            fontSize: "13px",
          }}
        >
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <span>
            La ventana de agrupación está en 0: cada mensaje del vendedor sale solo y el MPT siempre da 1.
            Súbela en Ajustes del pack en WordPress para que mida la consolidación de envíos.
          </span>
        </div>
      )}
    </div>
  );
};
